
import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ReferentStatus } from "@/types/states";
import { ReferentSelectionModal } from "./ReferentSelectionModal";

interface CampaignDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: any | null;
  onRefresh?: () => void;
}

export const CampaignDetailsDialog = ({
  open,
  onOpenChange,
  campaign,
  onRefresh
}: CampaignDetailsDialogProps) => {
  const [showReminderModal, setShowReminderModal] = useState(false);

  if (!campaign) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-none w-auto p-0 border-none bg-transparent [&>button]:hidden">
          <DialogTitle className="sr-only">Détails de la campagne</DialogTitle>
          <DialogDescription className="sr-only">
            Informations complètes sur la campagne {campaign.candidate_name} et ses référents
          </DialogDescription>
          <div className="w-[573px] p-6 bg-white rounded-3xl inline-flex flex-col justify-start items-start gap-6">
          {/* Header */}
          <div className="self-stretch inline-flex justify-between items-start">
            <div className="inline-flex flex-col justify-start items-start gap-1">
              <div className="justify-start text-gray-800 text-2xl font-medium font-['Bricolage_Grotesque']">
                Détails de la campagne
              </div>
              <div className="justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                Informations complètes sur la campagne et ses référents
              </div>
            </div>
            <button 
              onClick={() => onOpenChange(false)}
              className="relative hover:bg-gray-100 rounded-full p-1 transition-colors"
            >
              <div data-svg-wrapper className="relative">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 8L12 12M12 12L16 16M12 12L16 8M12 12L8 16" stroke="#8F949D" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
            </button>
          </div>

          {/* Détails du candidat */}
          <div className="self-stretch flex flex-col justify-start items-start gap-3">
            <div className="justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
              Détails du candidat
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-4">
              <div className="self-stretch inline-flex justify-between items-center">
                {/* Nom */}
                <div className="flex justify-start items-center gap-2">
                  <div className="p-2 bg-teal-400/10 rounded-lg inline-flex flex-col justify-start items-start gap-2.5 overflow-hidden">
                    <div data-svg-wrapper className="relative">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.10671 7.24683C8.04004 7.24016 7.96004 7.24016 7.88671 7.24683C6.30004 7.1935 5.04004 5.8935 5.04004 4.2935C5.04004 2.66016 6.36004 1.3335 8.00004 1.3335C9.63337 1.3335 10.96 2.66016 10.96 4.2935C10.9534 5.8935 9.69337 7.1935 8.10671 7.24683Z" stroke="#40E0D0" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M4.77348 9.7065C3.16014 10.7865 3.16014 12.5465 4.77348 13.6198C6.60681 14.8465 9.61348 14.8465 11.4468 13.6198C13.0601 12.5398 13.0601 10.7798 11.4468 9.7065C9.62014 8.4865 6.61348 8.4865 4.77348 9.7065Z" stroke="#40E0D0" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <div className="w-40 inline-flex flex-col justify-start items-start gap-px">
                    <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-['Bricolage_Grotesque']">
                      {campaign.candidate_name}
                    </div>
                    <div className="self-stretch justify-start text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque']">
                      Nom
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="flex justify-start items-center gap-2">
                  <div className="p-2 bg-yellow-400/10 rounded-lg inline-flex flex-col justify-start items-start gap-2.5 overflow-hidden">
                    <div data-svg-wrapper className="relative">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.333 13.6668H4.66634C2.66634 13.6668 1.33301 12.6668 1.33301 10.3335V5.66683C1.33301 3.3335 2.66634 2.3335 4.66634 2.3335H11.333C13.333 2.3335 14.6663 3.3335 14.6663 5.66683V10.3335C14.6663 12.6668 13.333 13.6668 11.333 13.6668Z" stroke="#FFC506" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M11.3337 6L9.24699 7.66667C8.56032 8.21333 7.43366 8.21333 6.74699 7.66667L4.66699 6" stroke="#FFC506" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <div className="w-40 inline-flex flex-col justify-start items-start gap-px">
                    <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-['Bricolage_Grotesque']">
                      {campaign.candidate_email}
                    </div>
                    <div className="self-stretch justify-start text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque']">
                      E-mail
                    </div>
                  </div>
                </div>
              </div>

              <div className="self-stretch inline-flex justify-between items-center gap-4">
                {/* Position cible */}
                <div className="flex justify-start items-center gap-2 flex-1 min-w-0">
                  <div className="p-2 bg-green-500/10 rounded-lg inline-flex flex-col justify-start items-start gap-2.5 overflow-hidden flex-shrink-0">
                    <div data-svg-wrapper className="relative">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.33289 14.6667H10.6662C13.3462 14.6667 13.8262 13.5933 13.9662 12.2867L14.4662 6.95333C14.6462 5.32667 14.1796 4 11.3329 4H4.66623C1.81956 4 1.35289 5.32667 1.53289 6.95333L2.03289 12.2867C2.17289 13.5933 2.65289 14.6667 5.33289 14.6667Z" stroke="#32CD32" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5.33301 4.00016V3.46683C5.33301 2.28683 5.33301 1.3335 7.46634 1.3335H8.53301C10.6663 1.3335 10.6663 2.28683 10.6663 3.46683V4.00016" stroke="#32CD32" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9.33366 8.66667V9.33333C9.33366 9.34 9.33366 9.34 9.33366 9.34667C9.33366 10.0733 9.32699 10.6667 8.00033 10.6667C6.68033 10.6667 6.66699 10.08 6.66699 9.35333V8.66667C6.66699 8 6.66699 8 7.33366 8H8.66699C9.33366 8 9.33366 8 9.33366 8.66667Z" stroke="#32CD32" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14.433 7.3335C12.893 8.4535 11.133 9.12016 9.33301 9.34683" stroke="#32CD32" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M1.74707 7.51318C3.24707 8.53985 4.9404 9.15985 6.66707 9.35318" stroke="#32CD32" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <div className="inline-flex flex-col justify-start items-start gap-px min-w-0 flex-1">
                    <div className="text-gray-800 text-sm font-normal font-['Bricolage_Grotesque'] leading-tight break-words">
                      {campaign.target_position}
                    </div>
                    <div className="text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque']">
                      Poste visé
                    </div>
                  </div>
                </div>

                {/* Date de création */}
                <div className="flex justify-start items-center gap-2 flex-shrink-0">
                  <div className="p-2 bg-rose-500/10 rounded-lg inline-flex flex-col justify-start items-start gap-2.5 overflow-hidden">
                    <div data-svg-wrapper className="relative">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.33301 1.3335V3.3335" stroke="#FF515D" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10.667 1.3335V3.3335" stroke="#FF515D" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2.33301 6.06006H13.6663" stroke="#FF515D" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 5.66683V11.3335C14 13.3335 13 14.6668 10.6667 14.6668H5.33333C3 14.6668 2 13.3335 2 11.3335V5.66683C2 3.66683 3 2.3335 5.33333 2.3335H10.6667C13 2.3335 14 3.66683 14 5.66683Z" stroke="#FF515D" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M7.99666 9.13314H8.00265" stroke="#FF515D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5.52987 9.13314H5.53585" stroke="#FF515D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5.52987 11.1331H5.53585" stroke="#FF515D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <div className="w-40 inline-flex flex-col justify-start items-start gap-px">
                    <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-['Bricolage_Grotesque']">
                      {formatDate(campaign.created_at)}
                    </div>
                    <div className="self-stretch justify-start text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque']">
                      Date de création
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Afficher si le candidat a refusé */}
          {campaign.candidate_responses?.[0]?.response_type === 'refuse' && (
            <div className="self-stretch p-4 bg-red-50 rounded-xl border border-red-200 flex flex-col justify-start items-start gap-2">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-red-400/20 rounded-lg">
                  <div data-svg-wrapper className="relative">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 1.3335C4.32 1.3335 1.33333 4.32016 1.33333 8.00016C1.33333 11.6802 4.32 14.6668 8 14.6668C11.68 14.6668 14.6667 11.6802 14.6667 8.00016C14.6667 4.32016 11.68 1.3335 8 1.3335ZM10.3867 9.72683C10.5467 9.88683 10.5467 10.1468 10.3867 10.3068C10.3067 10.3868 10.2 10.4268 10.0933 10.4268C9.98667 10.4268 9.88 10.3868 9.8 10.3068L8 8.50683L6.2 10.3068C6.12 10.3868 6.01333 10.4268 5.90667 10.4268C5.8 10.4268 5.69333 10.3868 5.61333 10.3068C5.45333 10.1468 5.45333 9.88683 5.61333 9.72683L7.41333 7.92683L5.61333 6.12683C5.45333 5.96683 5.45333 5.70683 5.61333 5.54683C5.77333 5.38683 6.03333 5.38683 6.19333 5.54683L7.99333 7.34683L9.79333 5.54683C9.95333 5.38683 10.2133 5.38683 10.3733 5.54683C10.5333 5.70683 10.5333 5.96683 10.3733 6.12683L8.57333 7.92683L10.3867 9.72683Z" fill="#FF515D"/>
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-red-800 text-sm font-medium font-['Bricolage_Grotesque']">
                    Le candidat a refusé de participer
                  </div>
                  {campaign.candidate_responses[0].refusal_reason && (
                    <div className="text-red-700 text-xs font-normal font-['Bricolage_Grotesque'] mt-1">
                      Raison : {campaign.candidate_responses[0].refusal_reason}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Références */}
          <div className="self-stretch flex flex-col justify-start items-start gap-3">
            <div className="justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
              Références
            </div>
            
            {/* Cas spécial : En attente candidat */}
            {campaign.status === 'En attente candidat' ? (
              <div className="self-stretch p-4 bg-yellow-50 rounded-xl border border-yellow-200 flex flex-col justify-start items-start gap-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-yellow-400/20 rounded-lg">
                    <div data-svg-wrapper className="relative">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 1.3335C4.32 1.3335 1.33333 4.32016 1.33333 8.00016C1.33333 11.6802 4.32 14.6668 8 14.6668C11.68 14.6668 14.6667 11.6802 14.6667 8.00016C14.6667 4.32016 11.68 1.3335 8 1.3335ZM8 10.6668C7.63333 10.6668 7.33333 10.3668 7.33333 10.0002V8.00016C7.33333 7.6335 7.63333 7.3335 8 7.3335C8.36667 7.3335 8.66667 7.6335 8.66667 8.00016V10.0002C8.66667 10.3668 8.36667 10.6668 8 10.6668ZM8.66667 5.3335H7.33333V4.00016H8.66667V5.3335Z" fill="#FFC506"/>
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
                      En attente des référents
                    </div>
                    <div className="text-gray-600 text-xs font-normal font-['Bricolage_Grotesque']">
                      Le candidat n'a pas encore renseigné ses référents
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => setShowReminderModal(true)}
                  className="px-4 py-2 bg-yellow-400 hover:bg-yellow-500 rounded-lg transition-colors"
                >
                  <div className="text-white text-sm font-medium font-['Bricolage_Grotesque']">
                    Relancer le candidat
                  </div>
                </button>
              </div>
            ) : (
              <div className="self-stretch flex flex-col justify-start items-start gap-2">
                {campaign.referents?.map((referent: any) => (
                <div key={referent.id} className="self-stretch p-3 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex justify-between items-start">
                  <div className="w-96 inline-flex flex-col justify-start items-start gap-1">
                    <div className="inline-flex justify-start items-start gap-1">
                      <div className="justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
                        {referent.name}
                      </div>
                      <div className={`px-2 py-1 rounded-full flex justify-center items-center gap-2.5 ${
                        (referent.status === ReferentStatus.COMPLETE || campaign.status === 'Terminé')
                          ? 'bg-emerald-50' 
                          : 'bg-blue-50'
                      }`}>
                        <div className={`justify-center text-[10px] font-normal font-['Bricolage_Grotesque'] ${
                          (referent.status === ReferentStatus.COMPLETE || campaign.status === 'Terminé')
                            ? 'text-green-500' 
                            : 'text-blue-500'
                        }`}>
                          {referent.status || ReferentStatus.INVITE}
                        </div>
                      </div>
                    </div>
                    <div className="inline-flex justify-start items-center gap-1.5">
                      <div className="justify-start text-gray-800 text-xs font-normal font-['Bricolage_Grotesque']">
                        {referent.email}
                      </div>
                      <div className="w-2.5 h-0 origin-top-left rotate-90 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>
                      <div className="justify-start text-gray-800 text-xs font-normal font-['Bricolage_Grotesque']">
                        {referent.relation_type}
                      </div>
                      <div className="w-2.5 h-0 origin-top-left rotate-90 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>
                      <div className="justify-start text-gray-800 text-xs font-normal font-['Bricolage_Grotesque']">
                        {referent.reminders_sent_count} rappel(s)
                      </div>
                    </div>
                    <div className="inline-flex justify-start items-center gap-2">
                      <div className="flex justify-start items-center gap-0.5">
                        <div data-svg-wrapper className="relative">
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.5 10.25H3.5C2 10.25 1 9.5 1 7.75V4.25C1 2.5 2 1.75 3.5 1.75H8.5C10 1.75 11 2.5 11 4.25V7.75C11 9.5 10 10.25 8.5 10.25Z" stroke="#8F949D" strokeWidth="0.8" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M8.5 4.5L6.935 5.75C6.42 6.16 5.575 6.16 5.06 5.75L3.5 4.5" stroke="#8F949D" strokeWidth="0.8" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="justify-start text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque']">
                          {referent.reminders_sent_count} rappels
                        </div>
                      </div>
                      <div className="flex justify-start items-center gap-0.5">
                        <div data-svg-wrapper className="relative">
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7.78996 5.99996C7.78996 6.98996 6.98996 7.78996 5.99996 7.78996C5.00996 7.78996 4.20996 6.98996 4.20996 5.99996C4.20996 5.00996 5.00996 4.20996 5.99996 4.20996C6.98996 4.20996 7.78996 5.00996 7.78996 5.99996Z" stroke="#8F949D" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M5.99992 10.1349C7.76492 10.1349 9.40992 9.09486 10.5549 7.29486C11.0049 6.58986 11.0049 5.40486 10.5549 4.69986C9.40992 2.89986 7.76492 1.85986 5.99992 1.85986C4.23492 1.85986 2.58992 2.89986 1.44492 4.69986C0.994922 5.40486 0.994922 6.58986 1.44492 7.29486C2.58992 9.09486 4.23492 10.1349 5.99992 10.1349Z" stroke="#8F949D" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="justify-start text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque']">
                          {referent.email_opens_count} ouvertures
                        </div>
                      </div>
                      <div className="flex justify-start items-center gap-0.5">
                        <div data-svg-wrapper className="relative">
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.19719 10.367C5.81869 10.8595 5.03119 10.6025 5.01619 9.98148L4.89669 5.05148C4.89372 4.93315 4.92271 4.8162 4.98059 4.71295C5.03847 4.6097 5.12312 4.52396 5.22561 4.46476C5.32811 4.40555 5.44467 4.37507 5.56303 4.37651C5.68139 4.37796 5.79717 4.41128 5.89819 4.47298L10.1082 7.04148C10.6382 7.36498 10.4672 8.17548 9.85169 8.25698L7.85819 8.52048C7.68519 8.54348 7.52819 8.63398 7.42219 8.77198L6.19719 10.367Z" stroke="#8F949D" strokeWidth="0.8" strokeLinejoin="round"/>
                            <path d="M1.89844 4.37502H3.14844M5.02344 2.62402V1.37402M3.40744 2.88252L2.52344 1.99902M6.63944 2.88252L7.52344 1.99902" stroke="#8F949D" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="justify-start text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque']">
                          {referent.email_clicks_count} clics
                        </div>
                      </div>
                    </div>
                    {/* Afficher la raison de refus si le référent a refusé */}
                    {referent.status === 'Refusé' && referent.refusal_reason && (
                      <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-200">
                        <div className="text-red-800 text-xs font-medium font-['Bricolage_Grotesque'] mb-1">
                          Raison du refus :
                        </div>
                        <div className="text-red-700 text-xs font-normal font-['Bricolage_Grotesque']">
                          {referent.refusal_reason}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={`px-2.5 py-2 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-start items-center gap-1 overflow-hidden ${
                    (referent.status === ReferentStatus.COMPLETE || campaign.status === 'Terminé') ? 'opacity-0' : ''
                  }`}>
                    <div className="justify-center text-neutral-400 text-xs font-medium font-['Bricolage_Grotesque']">
                      Relancer
                    </div>
                  </div>
                </div>
              ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Modal de relance candidat/référents */}
    <ReferentSelectionModal
      open={showReminderModal}
      onOpenChange={setShowReminderModal}
      campaign={campaign}
      onSuccess={() => {
        setShowReminderModal(false);
        onRefresh?.();
      }}
    />
  </>
  );
};
